import React, { useEffect, useMemo, useState } from "react";
import config from "../config/config";
import { useUser } from "../context/UserContext";
import useIsMobile from "../hooks/useIsMobile";

const API_URL = config.API_URL;

async function api(path, options = {}) {
  const res = await fetch(`${API_URL}${path}`, {
    credentials: "include",
    headers: { "Content-Type": "application/json", ...(options.headers || {}) },
    ...options,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.message || "요청 실패");
  return data;
}

function Reservation() {
  const isMobile = useIsMobile();
  const { authority } = useUser();
  const isAdmin = Number(authority) >= 5;

  const [spaces, setSpaces] = useState([]);
  const [selectedSpaceId, setSelectedSpaceId] = useState(null);
  const [desks, setDesks] = useState([]);
  const [myReservations, setMyReservations] = useState([]);
  const [adminReservations, setAdminReservations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [form, setForm] = useState({ desk_id: "", start_time: "09:00", end_time: "18:00", memo: "" });
  const [newSpace, setNewSpace] = useState({ name: "", description: "", location: "" });
  const [newDesk, setNewDesk] = useState({ space_id: "", desk_code: "", name: "" });
  const [newPolicy, setNewPolicy] = useState({ target_type: "space", target_id: "", rule: "" });

  // 엑셀 다운로드 필터
  const [excelFilter, setExcelFilter] = useState({
    start_date: new Date(new Date().setMonth(new Date().getMonth() - 1)).toISOString().slice(0, 10),
    end_date: new Date().toISOString().slice(0, 10),
    space_id: "",
    startup_id: ""
  });

  const selectedSpace = useMemo(
    () => spaces.find((item) => String(item.space_id) === String(selectedSpaceId)),
    [spaces, selectedSpaceId]
  );

  const display = (value) => {
    if (value === null || value === undefined) return "-";
    if (typeof value === "string") return value;
    return JSON.stringify(value);
  };

  const formatTime = (value) => {
    if (!value) return "-";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return String(value).slice(11, 16);
    return new Intl.DateTimeFormat("ko-KR", {
      timeZone: "Asia/Seoul",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    }).format(d);
  };

  const loadSpaces = async () => {
    console.log("📍 loadSpaces called, current selectedSpaceId:", selectedSpaceId);
    const data = await api("/api/reservation/spaces");
    setSpaces(data.value || []);
    const firstSpaceId = data.value?.[0]?.space_id;
    console.log("📍 loadSpaces got spaces:", data.value?.length, "firstSpaceId:", firstSpaceId);
    if (!selectedSpaceId && firstSpaceId) {
      console.log("📍 Setting selectedSpaceId to:", String(firstSpaceId));
      setSelectedSpaceId(String(firstSpaceId));
      return String(firstSpaceId); // 선택된 space_id 반환
    }
    return selectedSpaceId;
  };

  const loadDesks = async (spaceId = selectedSpaceId) => {
    console.log("🔍 loadDesks called with:", {
      paramSpaceId: spaceId,
      stateSelectedSpaceId: selectedSpaceId,
      date: date,
      stack: new Error().stack
    });
    if (!spaceId) {
      console.warn("⚠️ loadDesks skipped: spaceId is", spaceId);
      return;
    }
    const data = await api(`/api/reservation/spaces/${spaceId}/desks?date=${date}`);
    setDesks(data.value || []);
  };

  const loadMyReservations = async () => {
    const data = await api("/api/reservation/my");
    setMyReservations(data.value || []);
  };

  const loadAdminReservations = async () => {
    if (!isAdmin) return;
    const data = await api(`/api/reservation/admin?date=${date}`);
    setAdminReservations(data.value || []);
  };

  const refreshAll = async () => {
    console.log("🔄 refreshAll called");
    setLoading(true);
    try {
      const spaceId = await loadSpaces(); // space_id 받기
      console.log("🔄 refreshAll got spaceId:", spaceId);
      await Promise.all([loadMyReservations(), loadAdminReservations()]);
      if (spaceId) {
        console.log("🔄 refreshAll calling loadDesks with:", spaceId);
        await loadDesks(spaceId); // 받은 space_id 사용
      } else {
        console.warn("🔄 refreshAll skipping loadDesks: no spaceId");
      }
    } catch (error) {
      console.error("Reservation error:", error);
      alert(`에러 발생: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refreshAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    console.log("⚡ useEffect[selectedSpaceId, date] triggered:", {
      selectedSpaceId,
      date
    });
    if (selectedSpaceId) {
      console.log("⚡ useEffect calling loadDesks with:", selectedSpaceId);
      loadDesks(selectedSpaceId).catch((error) => {
        console.error("loadDesks error:", error);
        alert(error.message);
      });
    } else {
      console.log("⚡ useEffect skipped: selectedSpaceId is", selectedSpaceId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSpaceId, date]);

  useEffect(() => {
    if (isAdmin) {
      loadAdminReservations().catch((error) => alert(error.message));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date, isAdmin]);

  const handleReserve = async () => {
    try {
      if (!selectedSpaceId || !form.desk_id) throw new Error("공간과 좌석을 선택해 주세요.");
      const payload = {
        space_id: Number(selectedSpaceId),
        desk_id: Number(form.desk_id),
        reserve_date: date,
        start_time: `${date}T${form.start_time}:00+09:00`,
        end_time: `${date}T${form.end_time}:00+09:00`,
        memo: form.memo ? { purpose: form.memo } : null,
      };
      await api("/api/reservation", { method: "POST", body: JSON.stringify(payload) });
      alert("예약이 생성되었습니다.");
      setForm((prev) => ({ ...prev, memo: "", desk_id: "" }));
      await refreshAll();
    } catch (error) {
      alert(error.message);
    }
  };

  const handleAction = async (path, method = "POST") => {
    try {
      await api(path, { method });
      await refreshAll();
      alert("완료되었습니다.");
    } catch (error) {
      alert(error.message);
    }
  };

  const handleCreateSpace = async () => {
    try {
      await api("/api/reservation/admin/spaces", { method: "POST", body: JSON.stringify(newSpace) });
      setNewSpace({ name: "", description: "", location: "" });
      await refreshAll();
      alert("공간이 생성되었습니다.");
    } catch (error) {
      alert(error.message);
    }
  };

  const handleCreateDesk = async () => {
    try {
      await api("/api/reservation/admin/desks", {
        method: "POST",
        body: JSON.stringify({ ...newDesk, space_id: Number(newDesk.space_id) }),
      });
      setNewDesk({ space_id: "", desk_code: "", name: "" });
      await refreshAll();
      alert("좌석이 생성되었습니다.");
    } catch (error) {
      alert(error.message);
    }
  };

  const handleCreatePolicy = async () => {
    try {
      const rule = JSON.parse(newPolicy.rule);
      await api("/api/reservation/admin/policies", {
        method: "POST",
        body: JSON.stringify({ ...newPolicy, target_id: Number(newPolicy.target_id), rule }),
      });
      setNewPolicy({ target_type: "space", target_id: "", rule: "" });
      alert("정책이 생성되었습니다.");
    } catch (error) {
      alert(error.message);
    }
  };

  const handleDownloadExcel = async () => {
    try {
      const params = new URLSearchParams();
      if (excelFilter.start_date) params.append("start_date", excelFilter.start_date);
      if (excelFilter.end_date) params.append("end_date", excelFilter.end_date);
      if (excelFilter.space_id) params.append("space_id", excelFilter.space_id);
      if (excelFilter.startup_id) params.append("startup_id", excelFilter.startup_id);

      const url = `${API_URL}/api/reservation/admin/download-excel?${params.toString()}`;
      const res = await fetch(url, { credentials: "include" });

      if (!res.ok) {
        const error = await res.json().catch(() => ({ message: "다운로드 실패" }));
        throw new Error(error.message);
      }

      const blob = await res.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = downloadUrl;

      // 파일명 추출 (Content-Disposition 헤더에서)
      const disposition = res.headers.get("Content-Disposition");
      let filename = "좌석예약내역.xlsx";
      if (disposition && disposition.includes("filename=")) {
        const matches = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(disposition);
        if (matches != null && matches[1]) {
          filename = decodeURIComponent(matches[1].replace(/['"]/g, ""));
        }
      }

      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);

      alert("엑셀 파일이 다운로드되었습니다.");
    } catch (error) {
      alert(error.message);
    }
  };

  if (loading) return <div style={styles.loading}>Loading...</div>;

  return (
    <div style={isMobile ? mobileStyles.wrap : styles.wrap}>
      <div style={styles.header}>
        <h2 style={styles.title}>비지정석 예약</h2>
        <input type="date" value={date} onChange={(e) => setDate(e.target.value)} style={styles.dateInput} />
      </div>

      <div style={styles.section}>
        <h3 style={styles.sectionTitle}>공간</h3>
        <div style={styles.rowWrap}>
          {spaces.map((space) => (
            <button
              key={space.space_id}
              onClick={() => {
                setSelectedSpaceId(String(space.space_id));
                setForm((prev) => ({ ...prev, desk_id: "" }));
              }}
              style={{
                ...styles.pill,
                ...(String(space.space_id) === String(selectedSpaceId) ? styles.pillActive : {}),
              }}
            >
              {space.name}
            </button>
          ))}
        </div>
        {selectedSpace && <div style={styles.meta}>위치: {display(selectedSpace.location)} / 좌석수: {selectedSpace.desk_count || 0}</div>}
      </div>

      <div style={styles.grid2}>
        <div style={styles.section}>
          <h3 style={styles.sectionTitle}>좌석 선택</h3>

          {/* 레벨업 스튜디오-7 좌석 배치도 */}
          {selectedSpace && String(selectedSpace.name).includes("레벨업 스튜디오-7") && (
            <div style={{ marginBottom: "16px", padding: "12px", backgroundColor: "#f8f9fa", borderRadius: "8px" }}>
              <h4 style={{ margin: "0 0 8px 0", fontSize: "14px", fontWeight: "600" }}>📍 좌석 배치도</h4>
              <div style={{ backgroundColor: "#fff", padding: "8px", borderRadius: "4px", textAlign: "center" }}>
                <img
                  src="/images/levelup-studio7-layout.png"
                  alt="레벨업 스튜디오-7 좌석 배치도"
                  style={{
                    maxWidth: "100%",
                    height: "auto",
                    border: "1px solid #dee2e6",
                    borderRadius: "4px"
                  }}
                  onError={(e) => {
                    e.target.style.display = "none";
                    e.target.nextSibling.style.display = "block";
                  }}
                />
                <div style={{ display: "none", padding: "20px", color: "#6c757d" }}>
                  좌석 배치도 이미지가 준비 중입니다.<br/>
                  <small>(/images/levelup-studio7-layout.png)</small>
                </div>
              </div>
            </div>
          )}

          <div style={styles.deskGrid}>
            {desks.map((desk) => {
              const isSelected = String(form.desk_id) === String(desk.desk_id);
              const isAvailable = desk.available;
              return (
                <button
                  key={desk.desk_id}
                  onClick={() => {
                    if (isAvailable) {
                      setForm((prev) => ({ ...prev, desk_id: String(desk.desk_id) }));
                    }
                  }}
                  style={{
                    ...styles.deskCard,
                    ...(isSelected ? styles.deskCardActive : {}),
                    ...(isAvailable ? {} : styles.deskCardBusy),
                  }}
                  disabled={!isAvailable}
                >
                  <div style={{ ...styles.deskCode, color: isSelected ? "#fff" : "inherit" }}>
                    {desk.desk_code}
                  </div>
                  <div style={{ ...styles.deskStatus, color: isSelected ? "#f0f0f0" : "#666" }}>
                    {isAvailable ? "예약 가능" : "예약됨"}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        <div style={styles.section}>
          <h3 style={styles.sectionTitle}>예약 생성</h3>
          <div style={styles.formRow}>
            <label style={styles.label}>시작</label>
            <input type="time" value={form.start_time} onChange={(e) => setForm((prev) => ({ ...prev, start_time: e.target.value }))} style={styles.input} />
          </div>
          <div style={styles.formRow}>
            <label style={styles.label}>종료</label>
            <input type="time" value={form.end_time} onChange={(e) => setForm((prev) => ({ ...prev, end_time: e.target.value }))} style={styles.input} />
          </div>
          <div style={styles.formRow}>
            <label style={styles.label}>메모</label>
            <input value={form.memo} onChange={(e) => setForm((prev) => ({ ...prev, memo: e.target.value }))} style={styles.input} placeholder="사용 목적" />
          </div>
          <button onClick={handleReserve} style={styles.primaryButton}>예약하기</button>
        </div>
      </div>

      <div style={styles.section}>
        <h3 style={styles.sectionTitle}>내 예약</h3>
        <div style={styles.list}>
          {myReservations.map((item) => (
            <div key={item.reservation_id} style={styles.card}>
              <div>{display(item.space_name)} / {display(item.desk_code)}</div>
              <div>{item.reserve_date} {formatTime(item.start_time)} ~ {formatTime(item.end_time)}</div>
              <div>상태: {item.status}</div>
              <div style={styles.actions}>
                <button onClick={() => handleAction(`/api/reservation/${item.reservation_id}/checkin`)} style={styles.button}>체크인</button>
                <button onClick={() => handleAction(`/api/reservation/${item.reservation_id}/checkout`)} style={styles.button}>체크아웃</button>
                <button onClick={() => handleAction(`/api/reservation/${item.reservation_id}`, "DELETE")} style={styles.dangerButton}>취소</button>
              </div>
            </div>
          ))}
          {!myReservations.length && <div style={styles.empty}>예약 내역이 없습니다.</div>}
        </div>
      </div>

      {isAdmin && (
        <>
          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>관리자 예약 현황</h3>
            <div style={styles.list}>
              {adminReservations.map((item) => (
                <div key={item.reservation_id} style={styles.card}>
                  <div>{item.startup_id} / {display(item.space_name)} / {display(item.desk_code)}</div>
                  <div>{item.reserve_date} {formatTime(item.start_time)} ~ {formatTime(item.end_time)}</div>
                  <div>상태: {item.status}</div>
                </div>
              ))}
              {!adminReservations.length && <div style={styles.empty}>관리자 예약이 없습니다.</div>}
            </div>
          </div>

          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>예약 내역 엑셀 다운로드</h3>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px", marginBottom: "12px" }}>
              <div>
                <label style={{ display: "block", marginBottom: "4px", fontSize: "14px", fontWeight: "500" }}>시작일</label>
                <input
                  type="date"
                  value={excelFilter.start_date}
                  onChange={(e) => setExcelFilter((prev) => ({ ...prev, start_date: e.target.value }))}
                  style={styles.input}
                />
              </div>
              <div>
                <label style={{ display: "block", marginBottom: "4px", fontSize: "14px", fontWeight: "500" }}>종료일</label>
                <input
                  type="date"
                  value={excelFilter.end_date}
                  onChange={(e) => setExcelFilter((prev) => ({ ...prev, end_date: e.target.value }))}
                  style={styles.input}
                />
              </div>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px", marginBottom: "12px" }}>
              <div>
                <label style={{ display: "block", marginBottom: "4px", fontSize: "14px", fontWeight: "500" }}>공간 필터 (선택)</label>
                <select
                  value={excelFilter.space_id}
                  onChange={(e) => setExcelFilter((prev) => ({ ...prev, space_id: e.target.value }))}
                  style={styles.input}
                >
                  <option value="">전체 공간</option>
                  {spaces.map((space) => (
                    <option key={space.space_id} value={space.space_id}>
                      {display(space.name)}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label style={{ display: "block", marginBottom: "4px", fontSize: "14px", fontWeight: "500" }}>스타트업 ID (선택)</label>
                <input
                  type="text"
                  placeholder="스타트업 ID"
                  value={excelFilter.startup_id}
                  onChange={(e) => setExcelFilter((prev) => ({ ...prev, startup_id: e.target.value }))}
                  style={styles.input}
                />
              </div>
            </div>
            <button onClick={handleDownloadExcel} style={{ ...styles.primaryButton, width: "100%" }}>
              엑셀 다운로드
            </button>
          </div>

          <div style={styles.grid3}>
            <div style={styles.section}>
              <h3 style={styles.sectionTitle}>공간 추가</h3>
              <input placeholder="공간명" value={newSpace.name} onChange={(e) => setNewSpace((prev) => ({ ...prev, name: e.target.value }))} style={styles.input} />
              <input placeholder="설명" value={newSpace.description} onChange={(e) => setNewSpace((prev) => ({ ...prev, description: e.target.value }))} style={styles.input} />
              <input placeholder="위치" value={newSpace.location} onChange={(e) => setNewSpace((prev) => ({ ...prev, location: e.target.value }))} style={styles.input} />
              <button onClick={handleCreateSpace} style={styles.primaryButton}>공간 생성</button>
            </div>

            <div style={styles.section}>
              <h3 style={styles.sectionTitle}>좌석 추가</h3>
              <select value={newDesk.space_id} onChange={(e) => setNewDesk((prev) => ({ ...prev, space_id: e.target.value }))} style={styles.input}>
                <option value="">공간 선택</option>
                {spaces.map((space) => (
                  <option key={space.space_id} value={space.space_id}>{space.name}</option>
                ))}
              </select>
              <input placeholder="좌석 코드 (예: A-01)" value={newDesk.desk_code} onChange={(e) => setNewDesk((prev) => ({ ...prev, desk_code: e.target.value }))} style={styles.input} />
              <input placeholder="좌석명" value={newDesk.name} onChange={(e) => setNewDesk((prev) => ({ ...prev, name: e.target.value }))} style={styles.input} />
              <button onClick={handleCreateDesk} style={styles.primaryButton}>좌석 생성</button>
            </div>

            <div style={styles.section}>
              <h3 style={styles.sectionTitle}>정책 추가</h3>
              <select value={newPolicy.target_type} onChange={(e) => setNewPolicy((prev) => ({ ...prev, target_type: e.target.value }))} style={styles.input}>
                <option value="space">space</option>
                <option value="desk">desk</option>
              </select>
              <input placeholder="target_id" value={newPolicy.target_id} onChange={(e) => setNewPolicy((prev) => ({ ...prev, target_id: e.target.value }))} style={styles.input} />
              <textarea
                placeholder='{"min_minutes":30,"max_minutes":480,"daily_limit_per_user":1}'
                value={newPolicy.rule}
                onChange={(e) => setNewPolicy((prev) => ({ ...prev, rule: e.target.value }))}
                style={{ ...styles.input, minHeight: 120, resize: "vertical" }}
              />
              <button onClick={handleCreatePolicy} style={styles.primaryButton}>정책 생성</button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

const styles = {
  wrap: {
    width: "100%",
    padding: 20,
    display: "flex",
    flexDirection: "column",
    gap: 16,
  },
  loading: { padding: 20 },
  header: { display: "flex", justifyContent: "space-between", alignItems: "center" },
  title: { margin: 0 },
  dateInput: { padding: 8, borderRadius: 8, border: "1px solid #ddd" },
  section: { background: "#fff", borderRadius: 16, padding: 16, boxShadow: "0 2px 10px rgba(0,0,0,0.06)" },
  sectionTitle: { margin: "0 0 12px 0", fontSize: 16 },
  rowWrap: { display: "flex", flexWrap: "wrap", gap: 8 },
  pill: { padding: "10px 14px", borderRadius: 999, border: "1px solid #d9d9e3", background: "#f7f7fb" },
  pillActive: { background: "#686769", color: "white" },
  meta: { marginTop: 10, color: "#666" },
  grid2: { display: "grid", gridTemplateColumns: "1.2fr 0.8fr", gap: 16 },
  grid3: { display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 16 },
  deskGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(120px, 1fr))", gap: 10 },
  deskCard: { border: "2px solid #ddd", borderRadius: 12, padding: 12, textAlign: "left", background: "#fff", cursor: "pointer", transition: "all 0.2s" },
  deskCardActive: { borderColor: "#686769", background: "#686769", color: "#fff", boxShadow: "0 4px 12px rgba(104,103,105,0.3)" },
  deskCardBusy: { opacity: 0.4, cursor: "not-allowed" },
  deskCode: { fontWeight: 700 },
  deskStatus: { fontSize: 12, color: "#666", marginTop: 4 },
  formRow: { display: "flex", flexDirection: "column", gap: 6, marginBottom: 12 },
  label: { fontSize: 12, color: "#666" },
  input: { padding: 10, borderRadius: 8, border: "1px solid #ddd", width: "100%", boxSizing: "border-box" },
  primaryButton: { marginTop: 8, padding: "10px 14px", borderRadius: 8, border: "none", background: "#686769", color: "white" },
  button: { padding: "8px 10px", borderRadius: 8, border: "1px solid #ddd", background: "#fff" },
  dangerButton: { padding: "8px 10px", borderRadius: 8, border: "1px solid #f2b8b5", background: "#fff0f0", color: "#c0392b" },
  actions: { display: "flex", gap: 8, flexWrap: "wrap", marginTop: 10 },
  list: { display: "flex", flexDirection: "column", gap: 12 },
  card: { border: "1px solid #eee", borderRadius: 12, padding: 12 },
  empty: { color: "#777" },
};

const mobileStyles = {
  wrap: { width: "100%", padding: 12 },
};

export default Reservation;
